import { NodeType } from '@/types/tree';

/**
 * Generate a tree node id for lookup. Must stay aligned with TreeNodeFactory.createNodeId.
 * Prefix is the NodeType string except sampleInPackage, which shares the live `sample-` dataId.
 */
export const generateNodeId = (type: NodeType, dataId: string | number): string => {
  const id = String(dataId);
  if (type === 'sampleInPackage') {
    return `sample-${id}`;
  }
  return `${type}-${id}`;
};

/**
 * Extract the entity ID from a data ID
 */
export const extractEntityIdFromDataId = (dataId: string): string => {
  const parts = dataId.split('-');
  return parts.slice(1).join('-'); // Handle IDs that might contain dashes
};

/**
 * API route key for data-package endpoints from a tree dataId (e.g. dataPackage-f4ZH3epA3cwb → f4ZH3epA3cwb).
 */
export function getDataPackageApiId(dataId: string): string {
  return extractEntityIdFromDataId(dataId);
}

/**
 * Extract the node type from a node ID
 */
export const extractNodeType = (nodeId: string): NodeType => {
  const parts = nodeId.split('-');
  return parts[0] as NodeType;
};

/** True when this node sits under a sample-like parent in the tree (live or RO). */
export function isSampleLikeNodeType(type: NodeType | string | undefined): boolean {
  return type === 'sample' || type === 'sampleAggregate' || type === 'sampleSnapshot' || type === 'sampleInPackage';
}

/**
 * Extracts numeric entity ID from dataId (e.g., "sample-123" -> 123)
 */
export function extractNumericEntityIdFromDataId(dataId: string | undefined): number {
  if (!dataId) return 0;

  const index = dataId.indexOf('-');
  if (index === -1 || index === dataId.length - 1) {
    throw new Error(`Invalid dataId format: "${dataId}"`);
  }

  const numericPart = dataId.substring(index + 1);
  const parsed = parseInt(numericPart, 10);

  if (isNaN(parsed)) {
    throw new Error(`Invalid numeric entity ID in dataId: "${dataId}"`);
  }

  return parsed;
}
